// Nihanth Naidu Kalisetti | Portfolio Data (MOCK)
// This file contains all portfolio data. Will be replaced with backend API calls.

export const personalInfo = {
  name: "NIHANTH NAIDU K",
  fullName: "Nihanth Naidu Kalisetti",
  roles: ["AI Engineer.", "RAG Architect.", "Agent Builder.", "Top AI Voice."],
  subtitle: "Building production GenAI systems that actually ship.",
  currentBadge: "Building agentic AI systems",
  photoUrl: `${process.env.PUBLIC_URL || ''}/images/nihanth-profile.png`,
  resumeUrl: `${process.env.PUBLIC_URL || ''}/Nihanth_Naidu_Resume.pdf`,
  email: "nihanthnaidu.kalisetti@my.liu.edu",
  linkedin: "linkedin.com/in/nihanthnaidu007",
  linkedinUrl: "https://linkedin.com/in/nihanthnaidu007",
  github: "github.com/nihanthnaidu007",
  githubUrl: "https://github.com/nihanthnaidu007",
  bio: "I'm an AI/ML Engineer who doesn't just experiment with AI - I ship it. From multi-agent LangGraph pipelines to QLoRA fine-tuned LLMs, I build GenAI systems that cut costs, reduce latency, and actually make it to production. Currently finishing my MS in AI at LIU Brooklyn while building systems that a lot of teams are still figuring out.",
  whatIBring: [
    { icon: "Brain", title: "Production GenAI", description: "RAG pipelines, agent architectures, LLM eval" },
    { icon: "Zap", title: "Speed to Ship", description: "From prototype to production with proper MLOps" },
    { icon: "Megaphone", title: "Thought Leadership", description: "10M+ impressions, community builder" }
  ]
};

export const stats = [
  { value: 4, suffix: "+", label: "Years ML Experience" },
  { value: 10, suffix: "M+", label: "LinkedIn Impressions", decimals: 0 },
  { value: 3, suffix: "\u00d7", label: "Top AI Voice" },
  { value: 12, suffix: "+", label: "Production AI Systems" },
  { value: 36, suffix: "%", label: "Review Time Saved" }
];

export const skillCategories = [
  {
    icon: "Bot",
    title: "AI & GenAI Core",
    color: "#00D4FF",
    skills: ["LangChain", "LangGraph", "LlamaIndex", "RAG Pipelines", "Multi-Agent Systems", "QLoRA/LoRA/PEFT", "Prompt Engineering", "LLM Fine-Tuning", "Claude Sonnet", "GPT", "Llama 3", "Hugging Face", "Sentence-Transformers", "vLLM"]
  },
  {
    icon: "Code",
    title: "Languages & Frameworks",
    color: "#39FF14",
    skills: ["Python", "Java", "JavaScript", "SQL", "FastAPI", "Flask", "Spring Boot", "React"]
  },
  {
    icon: "Database",
    title: "Vector DBs & Data",
    color: "#F5A623",
    skills: ["pgvector", "Pinecone", "ChromaDB", "FAISS", "PostgreSQL", "Redis", "scikit-learn", "PyTorch", "TensorFlow"]
  },
  {
    icon: "Cloud",
    title: "MLOps & Infrastructure",
    color: "#9B59B6",
    skills: ["Docker", "Kubernetes", "GitHub Actions", "Jenkins", "n8n", "MCP Servers", "LangSmith", "Weights & Biases"]
  },
  {
    icon: "Wrench",
    title: "Dev Tools",
    color: "#00D4FF",
    skills: ["Git", "Postman", "Jupyter", "VS Code", "PyCharm"]
  }
];

export const experiences = [
  {
    title: "AI Engineer Intern",
    company: "Cogent LLC",
    location: "Remote - Nebraska",
    dates: "Jun 2026 – Present",
    tags: ["INTERNSHIP", "REMOTE"],
    metrics: [],
    bullets: [
      "Building an AI data platform that unifies fragmented structured and unstructured sources into one queryable layer, with LLM-powered natural-language access over enterprise data",
      "Designing hybrid RAG retrieval (pgvector with keyword search and reranking) behind FastAPI services, so teams query data in plain language instead of writing SQL by hand",
      "Building ingestion and embedding pipelines that turn raw records and documents into versioned, searchable knowledge, with evaluation and guardrails that keep responses grounded and auditable"
    ]
  },
  {
    title: "Founding AI/ML Engineer",
    company: "UnityGrid AI",
    location: "Remote - Washington, DC",
    dates: "Jul 2024 \u2013 Jan 2025",
    tags: ["CONTRACT", "REMOTE"],
    metrics: ["36% \u2193 manual review", "<900ms p95 latency", "28% \u2193 infra costs"],
    bullets: [
      "Designed multi-agent LangChain workflows for semiconductor design validation, slashing manual review time by 36% across 3 engineering teams and turning multi-day back-and-forth into an automated pipeline",
      "Engineered a pgvector + Redis RAG system that drove p95 latency under 900ms and cut infrastructure costs by 28%. Both in production, both measurable",
      "Connected AI agents to internal microservices via MCP server endpoints and webhook triggers, cutting inter-service response time to under one second",
      "Established LangSmith and OpenAI Evals observability with continuous hallucination tracking, flipping regression detection from a post-deployment fire drill to a pre-release checkpoint"
    ]
  },
  {
    title: "Software Engineer",
    company: "Digit Insurance",
    location: "Hyderabad, India",
    dates: "Jul 2024 \u2013 Dec 2024",
    tags: ["FULL-TIME"],
    metrics: ["50K+ txns/day", "60% \u2193 deploy time", "40% \u2193 manual issues"],
    bullets: [
      "Architected scalable RESTful APIs in Java (Spring Boot) supporting 50,000+ daily transactions across policy, claims, and customer systems",
      "Killed 60% of deployment time by ripping out manual releases and replacing them with Docker containers and Jenkins CI/CD pipelines, improving release reliability",
      "Automated build verification between QA and DevOps, cutting manual production issues by 40%",
      "Added post-deployment alerting and rollback strategies to minimize downtime on failed updates"
    ]
  },
  {
    title: "AI/ML Engineer",
    company: "Futzen eTechnologies Pvt. Ltd.",
    location: "Visakhapatnam, India",
    dates: "Apr 2021 \u2013 Jun 2024",
    tags: ["PROMOTED", "3+ YRS"],
    metrics: ["Promoted in 2 yrs", "35% \u2193 prep time", "F1 \u2191 across all models"],
    bullets: [
      "Earned a promotion to AI Engineer within 2 years by delivering customer segmentation and behavior models using K-Means, decision trees, and collaborative filtering, all deployed across live production pipelines",
      "Rebuilt feature engineering pipelines from scratch, cutting model training prep time by 35% and lifting F1 scores across every production model deployment that followed",
      "Prototyped retrieval-based AI systems through early LLM API integrations, reducing the team's manual document search burden and laying the groundwork for what became the company's first production RAG system"
    ]
  },
  {
    title: "Project Intern",
    company: "Edge Flex Solutions",
    location: "Visakhapatnam, India",
    dates: "Dec 2023 \u2013 Mar 2024",
    tags: ["INTERNSHIP"],
    metrics: ["50% \u2191 accuracy", "20% \u2191 satisfaction", "95% STT accuracy"],
    bullets: [
      "Designed AI-driven virtual assistants, increasing response accuracy by 50%.",
      "Implemented reinforcement learning for smarter chatbot interactions, improving satisfaction by 20%.",
      "Integrated speech-to-text models, achieving 95% transcription accuracy."
    ]
  },
  {
    title: "Intern",
    company: "V Info Technologies",
    location: "Visakhapatnam, India",
    dates: "Jun 2023 \u2013 Jul 2023",
    tags: ["INTERNSHIP"],
    metrics: [],
    bullets: []
  },
  {
    title: "Project Intern",
    company: "VINTERN",
    location: "",
    dates: "Jan 2023 \u2013 Apr 2023",
    tags: ["INTERNSHIP"],
    metrics: [],
    bullets: []
  }
];

export const projects = [
  {
    title: "AXIOM",
    tagline: "Self-Correcting Adaptive RAG",
    description: "Self-correcting RAG that rewrites itself until it gets the answer right.",
    color: "#A855F7",
    stack: ["LangGraph", "BM25", "pgvector", "RRF", "Cross-Encoder Reranking", "Redis"],
    callouts: ["13-node cyclic pipeline", "Pluggable evaluator", "BM25 + pgvector + RRF fusion", "Full LangSmith tracing"],
    impactMetrics: ["30\u201350\u00d7 cache speedup", "~55% latency improvement", "Composite RAGAS ~0.53"],
    fullDescription: "Built a 13-node cyclic LangGraph RAG that scores every answer for faithfulness, relevance, and groundedness, then rewrites and re-retrieves below threshold. A pluggable evaluator (local critic, Claude, or RAGAS) plus a two-tier Redis semantic cache delivering 30\u201350\u00d7 faster responses on cache hits.",
    badge: "OPEN SOURCE",
    link: "https://github.com/nihanthnaidu007/AXIOM_Adaptive_RAG"
  },
  {
    title: "NIXUS SQL",
    tagline: "Read-Only Text-to-SQL Agent",
    description: 'Natural language in. Read-only SQL out. Refusal by construction.',
    color: "#00D4FF",
    stack: ["LangGraph", "Claude", "PostgreSQL", "pgvector", "sqlglot", "FastAPI"],
    callouts: ["14-node LangGraph agent", "SELECT-only Postgres role", "sqlglot AST validation"],
    impactMetrics: ["55/57 answerable queries", "10/10 unsafe requests refused", "Refusal by construction"],
    fullDescription: "Engineered a Text-to-SQL agent that is read-only by construction: enforced at the database layer via a SELECT-only Postgres role, not just a prompt. Answered 55 of 57 answerable benchmark queries and refused all 10 out-of-scope, write, or ambiguous requests.",
    badge: "OPEN SOURCE",
    link: "https://github.com/nihanthnaidu007/Nixus-Sql-Agent"
  },
  {
    title: "ResearchForge",
    tagline: "Hierarchical Multi-Agent Research System",
    description: "6 specialized agents. Zero LLM routing decisions. Full human-in-the-loop.",
    color: "#39FF14",
    stack: ["LangGraph Supervisor", "GPT-4o", "Tavily", "Postgres", "React"],
    callouts: ["6 specialized agents", "Send() parallel fact-check", "interrupt() outline approval", "Postgres checkpointing"],
    impactMetrics: ["~80% token savings on partial edits", "21/21 internal audit checks", "Zero LLM routing decisions"],
    fullDescription: "Orchestrated 6 agents under a deterministic LangGraph Supervisor with no LLM in any routing decision. Parallel fact-checking via the Send() API, human-in-the-loop outline approval via interrupt() before the full report is written, and Postgres checkpointing. Versioned regeneration skips unchanged sections, saving roughly 80% of tokens on partial edits.",
    badge: "OPEN SOURCE",
    link: "https://github.com/nihanthnaidu007/Research_Forge"
  },
  {
    title: "Darwin",
    tagline: "Self-Verifying Code Optimizer",
    description: "LLM rewrites don't get trusted. They get proven.",
    color: "#FF4D6D",
    stack: ["Python", "Docker Sandbox", "Static Analysis", "Golden I/O"],
    callouts: ["Golden I/O container tests", "Static-metric structural gate", "No model in the scoring path"],
    impactMetrics: ["~95% one-shot pass rate", "Zero regressions", "Never worse than baseline"],
    fullDescription: "Accepts an LLM rewrite only if it clears three objective gates: container tests against golden I/O, then a static-metric structural score, with no model anywhere in the scoring path. A never-worse-than-baseline guarantee delivers zero regressions at a ~95% one-shot pass rate.",
    badge: "OPEN SOURCE",
    link: "https://github.com/nihanthnaidu007/Darwin"
  },
  {
    title: "PharmaLLM",
    tagline: "QLoRA Fine-Tuning Pipeline",
    description: "Fine-tuned LLaMA 3.1 8B on 14,400 pharma regulatory samples. Live on HuggingFace.",
    color: "#F5A623",
    stack: ["LLaMA 3.1 8B", "QLoRA", "Unsloth", "W&B"],
    callouts: ["LoRA rank 128", "4-bit NF4 quantization", "CFR citation guardrails"],
    impactMetrics: ["14,400 training samples", "4 model versions in W&B", "ROUGE-L 0.387"],
    fullDescription: "Built an end-to-end QLoRA fine-tuning pipeline for LLaMA 3.1 8B (LoRA rank 128, 4-bit NF4 quantization via Unsloth), trained on 14,400 pharma regulatory samples. Tracked 4 model versions in Weights & Biases and deployed with CFR citation guardrails. Final ROUGE-L: 0.387.",
    badge: "LIVE",
    link: "https://github.com/nihanthnaidu007/PharmaLLM-QLoRA",
    liveLink: "https://huggingface.co/spaces/nihanthnaidu007/PharmaLLM-QLoRA"
  },
  {
    title: "SpectraVoice",
    tagline: "Screen-Aware Mac Voice Assistant",
    description: "A voice assistant that sees your screen and acts on it. Hands-free.",
    color: "#5B8CFF",
    stack: ["Python", "Whisper", "OpenAI", "Ollama", "Tavily"],
    callouts: ["Real-time screen analysis", "Function-calling actions", "Barge-in + conversation memory"],
    impactMetrics: ["Cloud or fully local LLMs", "Keyboard + mouse control", "Whisper speech-to-text"],
    fullDescription: "Built a hands-free macOS assistant that sees the screen in real time and acts on it through function calling: opening apps, typing, searching the web, and running terminal commands. Whisper speech-to-text with natural TTS, barge-in, and short-term conversation memory. Runs on OpenAI in the cloud or fully local via Ollama.",
    badge: "OPEN SOURCE",
    link: "https://github.com/nihanthnaidu007/SpectraVoice---Screen-Aware-Mac-Voice-Assistant"
  },
];

export const publishedPackages = [
  {
    name: "inputguard",
    tagline: "Adversarial Prompt Detection & Input Validation",
    description: "Zero-dependency pre-flight input clarity layer. Catches vague or underspecified prompts before they reach an LLM: five intent classifiers and eight rule categories, running locally in milliseconds with no external API calls.",
    color: "#00D4FF",
    install: "pip install inputguard",
    pypiUrl: "https://pypi.org/project/inputguard/",
    githubUrl: "https://github.com/nihanthnaidu007/Input_Guard"
  },
  {
    name: "ai-stamp",
    tagline: "Signed Provenance, PII Scanning & Policy Enforcement",
    description: "Provenance and compliance layer for LLM output. Wraps OpenAI and Anthropic calls with tamper-evident HMAC-signed audit records, PII detection, and policy enforcement. SQLite and PostgreSQL backends with Alembic migrations.",
    color: "#39FF14",
    install: "pip install ai-stamp",
    pypiUrl: "https://pypi.org/project/ai-stamp/",
    githubUrl: "https://github.com/nihanthnaidu007/ai-stamp"
  }
];

export const recognitions = [
  { icon: "Trophy", title: "3\u00d7 LinkedIn Top AI Voice", description: "10M+ impressions globally", color: "#00D4FF" },
  { icon: "GraduationCap", title: "Academic Excellence Award", description: "Centurion University B.Tech", color: "#F5A623" },
  { icon: "Award", title: "AWS Certified AI Practitioner", description: "Amazon Web Services", color: "#39FF14" }
];

export const navLinks = [
  { label: "About", href: "#about" },
  { label: "Skills", href: "#skills" },
  { label: "Experience", href: "#experience" },
  { label: "Python Packages", href: "#packages" },
  { label: "Projects", href: "#projects" },
  { label: "Education", href: "#education" },
  { label: "Contact", href: "#contact" }
];
